import { useUser } from "components/clerk";
import SlidePage from "components/general/SlidePage";
import TitledContainer from "components/general/TitledContainer";
import TreeGroupNode from "components/navigation/TreeGroupNode";
import { ArrowLeftIcon, Box, Button, H3, HStack, Icon, Input, InputField, Paragraph, ParagraphSmall, Pressable, ScrollView, Typography, View } from "components/ui";
import { usePathname } from "expo-router";
import { Check, ChevronDown, PenLine, X } from "lucide-react-native";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import Modal from "react-native-modal";
import { TreeNode, useGetGroupQuery } from "store/abcoApi";
import { SelectBaseProps, Value } from "types/select.types";
import { truncateText } from "utils/globalFunctions";
import { useDropdownComponent } from "utils/hooks/useDropdownComponent";
import { useIsMobile } from "utils/hooks/useIsMobile";
import SiteForm from "./SiteForm";

// import { Pressable } from "react-native";

interface AddressSelectProps extends SelectBaseProps {
	value?: Value | Value[];
	isMulti?: boolean;
	minWidth?: any;
	isLight?: boolean;
	showError?: boolean;
	allowedTypes?: "Site" | "Group" | "Both";
	matchMode?: "Group" | "Site";
	title?: string;
	dropdownButtonRef?: any;
	borderRadius?: number;
	disabled?: boolean;
}

function scrollTo(scrollViewRef: any, topViewRef: any, viewToScrollToRef: any, offset = 0) {
	const scrollView = scrollViewRef.current;
	const topView = topViewRef.current;
	const viewToScrollTo = viewToScrollToRef.current;
	if (!scrollView || !topView || !viewToScrollTo) {
		return;
	}

	// @ts-ignore
	topView.measure((_ox, _oy, _width, _height, _px, topViewY) => {
		// @ts-ignore
		viewToScrollTo.measure((_ox2, _oy2, _width2, _height2, _px2, viewToScrollToY) => {
			scrollView.scrollTo({
				x: 0,
				y: viewToScrollToY - topViewY + offset,
				animated: false,
			});
		});
	});
}

export const SiteSelect = forwardRef(({ onSelect, disabled, value, minWidth, isLight, isMulti, showError, allowedTypes, matchMode = "Site", title = "Site", ...props }: AddressSelectProps, ref) => {
	const { isSignedIn } = useUser();
	const [searchTerm, setSearchTerm] = useState<string>("");
	const { setDropdownComponent } = useDropdownComponent();
	const [selectedItem, setSelectedItem] = useState<any>(value);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);
	const firstElementRef = useRef(null);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [buttonWidth, setButtonWidth] = useState(0);
	const isMobile = useIsMobile();
	const pathname = usePathname();
	const [originalPathname, setOriginalPathname] = useState<string>("");
	const [selectedMultipleItems, setSelectedMultipleItems] = useState<any>([]);
	const [isNewMultiSelection, setIsNewMultiSelection] = useState(true);
	const scrollViewRef = useRef(null); // Reference for the ScrollView
	const elementRef = useRef(null); // Reference for the target element
	const internalDropdownRef = useRef(null);
	const dropdownButtonRef = props.dropdownButtonRef || internalDropdownRef;

	if (!isSignedIn) {
		return null;
	}
	// Expose open/close methods via ref
	useImperativeHandle(ref, () => ({
		open: () => open(),
		close: () => close(),
	}));
	const { data, isLoading, isFetching } = useGetGroupQuery({}, { skip: !isSignedIn });

	useEffect(() => {
		setSelectedItem(value);
	}, [value]); // 👈️ Add props as dependencies

	const open = () => {
		if (isMobile) {
			setIsOpen(true);
		} else {
			// @ts-ignore
			dropdownButtonRef?.current.measure((fx, fy, w, h, px, py) => {
				setPosition({ x: px, y: py + h });
				setButtonWidth(w);
				setIsOpen(true);
			});
		}
	};

	const close = () => {
		setIsOpen(false);
	};

	const toggleModal = () => {
		setIsOpen(!isOpen);
	};

	const handleSelect = (node: TreeNode) => {
		if (node.type == "Group" && allowedTypes == "Site") {
			return;
		}

		if (node.type == "Site" && allowedTypes == "Group") {
			return;
		}

		var value = {
			id: node.id,
			name: node.name,
			siteId: node.siteId,
		} as unknown as Value;
		setSelectedItem(value);

		if (onSelect) {
			onSelect(value, 0);
		}

		close();
	};

	const handleOnChecked = (node: any) => {
		let newArray = selectedMultipleItems;
		let existingData = newArray.find((item: any) => item.id == node.id);
		if (existingData && !node.isSelected) {
			let newData = newArray.filter((item: any) => item.id != node.id);
			setSelectedMultipleItems(newData);
			return;
		}
		let newData = [...newArray, node];
		setSelectedMultipleItems(newData);
		setIsNewMultiSelection(false);
	};

	const handleSelectedLoad = (ref: any) => {
		if (ref.current) {
			scrollTo(dropdownRef, firstElementRef, ref, isMobile ? -82 : -70);
		}
	};

	const handleDoneSelect = () => {
		let values = selectedMultipleItems.map((item: any) => {
			return { siteId: item.siteId, name: item.name, id: item.id } as Value;
		});

		if (onSelect) {
			onSelect(values, 0);
		}

		setIsNewMultiSelection(true);
		close();
	};

	const handleClearSite = (e: any) => {
		e.preventDefault();

		if (!isMulti) {
			setSelectedItem(null);
			if (onSelect) {
				onSelect({ id: null, name: "" }, 0);
			}
		} else {
			setSelectedMultipleItems([]);
			if (onSelect) {
				onSelect([], 0);
			}
		}
	};

	return (
		<>
			<Box backgroundColor={isLight ? "$basicwhite" : "transparent"} position="relative" minWidth={minWidth ? minWidth : "auto"} ref={dropdownButtonRef}>
				<Typography
					sx={{
						top: isMobile ? -11 : 0,
						position: "absolute",
						zIndex: 1,
						lineHeight: 1.5,
						left: 11,
						background: "white",
						paddingLeft: 6,
						fontSize: 13,
						fontWeight: 700,
						paddingRight: 6,
						color: showError ? "$errordark" : "$basiblack",
					}}
				>
					{title}:
				</Typography>
				{!isMobile && !disabled && (
					<Button testID="site-select-button" variant="outline" borderRadius={props.borderRadius ? props.borderRadius : 12} onPress={open} borderWidth={1} borderColor={showError ? "$errordark" : "$neutral300"}>
						<HStack gap={6} alignItems="center" justifyContent="space-between" width={"$full"}>
							{!isMulti && <ParagraphSmall weight="normal">{selectedItem?.name ? truncateText(selectedItem?.name, 30) : "Select site..."} </ParagraphSmall>}
							{!isMulti && (!selectedItem || selectedItem.id == 0) && <Icon color="$primarylight" as={PenLine} />}
							{!isMulti && selectedItem && selectedItem.id != 0 && <Icon onPointerDown={(e) => handleClearSite(e)} color="$primarylight" as={X} />}
							{isMulti && <ParagraphSmall weight="normal">{selectedMultipleItems.length > 0 ? truncateText(selectedMultipleItems.map((item: any) => item.name).join(", "), 100) : "Select sites..."} </ParagraphSmall>}
							{isMulti && selectedMultipleItems.length == 0 && <Icon color="$primarylight" as={ChevronDown} />}
							{isMulti && selectedMultipleItems.length > 0 && <Icon onPointerDown={(e) => handleClearSite(e)} color="$primarylight" as={X} />}
						</HStack>
					</Button>
				)}
				{disabled && !isMobile && (
					<HStack gap={6} alignItems="center" justifyContent="space-between" width={"$full"}>
						{!isMulti && <ParagraphSmall weight="normal">{selectedItem?.name ? truncateText(selectedItem?.name, 30) : ""} </ParagraphSmall>}
						{isMulti && selectedMultipleItems.length > 0 ? truncateText(selectedMultipleItems.map((item: any) => item.name).join(", "), 100) : ""}
					</HStack>
				)}

				{/* Looks weird but this is a must to make styling work on both native and web. Will debug later on */}
				{isMobile && (
					<Button
						onPress={open}
						paddingHorizontal={8}
						paddingVertical={10}
						borderWidth={1}
						borderRadius={12}
						flexDirection="row"
						gap={6}
						alignItems="center"
						sx={{
							borderColor: "$neutral300",
							paddingHorizontal: 12,
							paddingVertical: 10,
							flexDirection: "row",
							alignItems: "center",
							gap: 6,
							width: isMobile ? "auto" : "100%",
						}}
					>
						<HStack gap={6} alignItems="center">
							{!isMulti && <Paragraph color={selectedItem?.name ? "$basicblack" : "$neutral300"}>{selectedItem?.name ? truncateText(selectedItem.name, 30) : "Select site..."}</Paragraph>}
							{isMulti && (
								<Paragraph color={selectedMultipleItems.length > 0 ? "$basicblack" : "$neutral300"}>
									{selectedMultipleItems.length > 0 ? truncateText(selectedMultipleItems.map((item: any) => item.name).join(", "), 100) : "Select sites..."}
								</Paragraph>
							)}
						</HStack>
						<Icon ml="auto" as={ChevronDown} color="$basicblack" />
					</Button>
				)}
			</Box>
			{isMobile && (
				<SlidePage
					customHeader={
						<Box backgroundColor="$primary" paddingHorizontal={16} paddingVertical={18}>
							<HStack space="xl" alignItems="center" mx="$0.5">
								<Pressable onPress={close}>
									<Icon color="$basicwhite" as={ArrowLeftIcon} m="$2" size="md" />
								</Pressable>
								<Input
									flex={1}
									backgroundColor="$basicwhite"
									variant="outline"
									borderRadius={12}
									borderColor="$borderBlue"
									size="md"
									px="$0"
									py="$3"
									isDisabled={false}
									isInvalid={false}
									isReadOnly={false}
									style={{
										zIndex: 100,
									}}
									isFocused={true}
									isHovered={true}
								>
									<InputField
										fontSize={"$h5desktop"}
										placeholder="Search for address..."
										value={searchTerm}
										onChangeText={(term) => {
											setSearchTerm(term);
										}}
										color="$basicblack"
										placeholderTextColor={"#cccccc"}
									/>
								</Input>
							</HStack>
						</Box>
					}
					toggleModal={toggleModal}
					isOpen={isOpen}
				>
					<Box ref={firstElementRef} paddingHorizontal={16} paddingVertical={12} paddingBottom={100}>
						<TreeGroupNode
							isFluid
							basicNode={data}
							level={0}
							defaultOpen={true}
							includeContextMenu={false}
							selectMode={isMulti ? "Multiple" : "Single"}
							onSelect={handleSelect}
							value={selectedItem}
							onMultipleSelect={handleOnChecked}
							selectedElementRef={elementRef}
							//scrollToElement={scrollToElement}
							allowedTypes={allowedTypes}
							matchMode={matchMode}
						/>
						{/* <TreeGroupSelect
							node={data}
							level={0}
							defaultOpen={true}
							onSelect={(node: any) => {
								handleSelect(node);
							}}
							onSelectedLoad={handleSelectedLoad}
							value={selectedItem}
							searchTerm={searchTerm}
							allowedTypes={allowedTypes}
						/> */}
					</Box>
				</SlidePage>
			)}

			{!isMobile && (
				<Modal animationIn={"fadeIn"} animationOut={"fadeOut"} scrollOffset={1} onBackdropPress={() => setIsOpen(false)} isVisible={isOpen}>
					<View bg="$basicwhite" overflow="hidden" borderRadius={12} height={700}>
						<HStack alignItems="center" justifyContent="space-between" bg="$lighttealxlight" paddingHorizontal={28} paddingVertical={20}>
							<H3>Address Select</H3>
							<Box>
								<Button onPress={() => setIsOpen(false)} gap={6}>
									<Icon as={X} />
									<Typography>Close</Typography>
								</Button>
							</Box>
						</HStack>

						<ScrollView sx={{ width: "100%", height: 400, padding: 28 }} ref={scrollViewRef}>
							{data?.children && data.children.length == 0 && (
								<Box mb={48} gap={12}>
									<TitledContainer title="Create site">
										<Box padding={24}>
											<SiteForm site={null} parentId={0} />
										</Box>
									</TitledContainer>
								</Box>
							)}

							{data?.children && data.children.length > 0 && (
								<>
									<TreeGroupNode
										basicNode={data}
										level={0}
										defaultOpen={true}
										includeContextMenu={false}
										selectMode={isMulti ? "Multiple" : "Single"}
										onSelect={handleSelect}
										value={selectedItem}
										onMultipleSelect={handleOnChecked}
										selectedElementRef={elementRef}
										//scrollToElement={scrollToElement}
										allowedTypes={allowedTypes}
										matchMode={matchMode}
									/>
								</>
							)}
						</ScrollView>
						{isMulti && (
							<HStack alignItems="center" justifyContent="center" bg="$lighttealxlight" paddingHorizontal={28} paddingVertical={10}>
								<Box>
									<Button sx={{ paddingHorizontal: 24, gap: 12 }} variant="secondary" onPress={handleDoneSelect}>
										<Typography weight="bold">Confirm</Typography>
										<Check color="black" />
									</Button>
								</Box>
							</HStack>
						)}
					</View>
				</Modal>
			)}
		</>
	);
});
